export const SUPPORTED_GMAIL_OPERATORS = ["from:", "to:", "subject:"] as const;

export type GmailOperatorKey = "from" | "to" | "subject";

const OPERATOR_KEYS: readonly GmailOperatorKey[] = ["from", "to", "subject"];

export type ParsedGmailQuery = {
  operators: Partial<Record<GmailOperatorKey, string>>;
  bareTokens: string[];
  unsupported: string[];
};

// Tokenize respecting double-quoted values, e.g. subject:"your invoice".
// Handles backslash-escaped quotes inside the quoted string.
function tokenize(input: string): string[] {
  const tokens: string[] = [];
  let buffer = "";
  let inQuotes = false;
  let escapeNext = false;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];

    if (escapeNext) {
      buffer += ch;
      escapeNext = false;
      continue;
    }
    if (ch === "\\" && inQuotes) {
      escapeNext = true;
      buffer += ch;
      continue;
    }
    if (ch === '"') {
      inQuotes = !inQuotes;
      buffer += ch;
      continue;
    }
    if (/\s/.test(ch) && !inQuotes) {
      if (buffer.length > 0) {
        tokens.push(buffer);
        buffer = "";
      }
      continue;
    }
    buffer += ch;
  }
  if (buffer.length > 0) tokens.push(buffer);
  return tokens;
}

function stripQuotes(value: string): string {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    return value.slice(1, -1);
  }
  return value;
}

export function parseGmailQuery(input: string): ParsedGmailQuery {
  const result: ParsedGmailQuery = { operators: {}, bareTokens: [], unsupported: [] };
  const trimmed = input.trim();
  if (!trimmed) return result;

  const tokens = tokenize(trimmed);

  for (const token of tokens) {
    // Bare "OR", leading "-", parens — unsupported boolean syntax.
    if (token === "OR" || token === "AND" || token.startsWith("-") || token.startsWith("(") || token.endsWith(")")) {
      result.unsupported.push(token);
      continue;
    }

    const colonIdx = token.indexOf(":");
    if (colonIdx > 0) {
      const rawKey = token.slice(0, colonIdx).toLowerCase();
      const rawValue = stripQuotes(token.slice(colonIdx + 1));
      if ((OPERATOR_KEYS as readonly string[]).includes(rawKey)) {
        const key = rawKey as GmailOperatorKey;
        // Duplicate operator: keep first, send subsequent to unsupported so
        // the user sees their extra clause didn't silently vanish.
        if (result.operators[key] !== undefined) {
          result.unsupported.push(token);
          continue;
        }
        result.operators[key] = rawValue;
        continue;
      }
      result.unsupported.push(token);
      continue;
    }

    // Bare phrase — strip surrounding quotes so `"monthly report"` matches
    // against plain text, not a literal quoted string.
    result.bareTokens.push(stripQuotes(token));
  }

  return result;
}

export type GmailMatchSample = {
  from?: string;
  to?: string;
  subject?: string;
  text?: string;
};

export type GmailMatchFieldResult = {
  field: GmailOperatorKey | "(any)";
  operator: `${GmailOperatorKey}:` | "(any)";
  filter: string;
  value: string;
  ok: boolean;
};

export type GmailMatchResult = {
  matches: boolean;
  fields: GmailMatchFieldResult[];
  unsupported: string[];
  hasConfiguredFilter: boolean;
};

function containsCI(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

export function evaluateGmailMatch(sample: GmailMatchSample, parsed: ParsedGmailQuery): GmailMatchResult {
  const fields: GmailMatchFieldResult[] = [];

  for (const key of OPERATOR_KEYS) {
    const filter = parsed.operators[key];
    if (typeof filter !== "string" || filter.length === 0) continue;
    const value = sample[key] ?? "";
    fields.push({
      field: key,
      operator: `${key}:`,
      filter,
      value,
      ok: containsCI(value, filter),
    });
  }

  if (parsed.bareTokens.length > 0) {
    const anywhere = [sample.from, sample.subject, sample.text, sample.to]
      .filter((v): v is string => typeof v === "string")
      .join("\n");
    for (const token of parsed.bareTokens) {
      fields.push({
        field: "(any)",
        operator: "(any)",
        filter: token,
        value: anywhere,
        ok: containsCI(anywhere, token),
      });
    }
  }

  const hasConfiguredFilter = fields.length > 0 || parsed.unsupported.length > 0;
  const matches = fields.every(f => f.ok);

  return { matches, fields, unsupported: parsed.unsupported, hasConfiguredFilter };
}

export type GmailSampleDefaults = {
  from?: string;
  to?: string;
  subject?: string;
  text?: string;
};

const TLD_RE = /\.[a-z]{2,}$/i;

function buildEmailLikeDefault(fragment: string): string {
  const trimmed = fragment.trim();
  if (trimmed.startsWith("@") && TLD_RE.test(trimmed)) {
    return `notify${trimmed}`.toLowerCase();
  }
  if (trimmed.indexOf("@") > 0 && TLD_RE.test(trimmed)) {
    return trimmed;
  }
  if (TLD_RE.test(trimmed)) {
    return `notify@${trimmed}`.toLowerCase();
  }
  return `${trimmed.toLowerCase()}@example.com`;
}

export function buildGmailSampleDefaults(parsed: ParsedGmailQuery): GmailSampleDefaults {
  const out: GmailSampleDefaults = {};

  if (parsed.operators.from) {
    out.from = buildEmailLikeDefault(parsed.operators.from);
  }
  if (parsed.operators.to) {
    out.to = buildEmailLikeDefault(parsed.operators.to);
  }
  if (parsed.operators.subject) {
    out.subject = `Your ${parsed.operators.subject.trim()} is ready`;
  }
  if (parsed.bareTokens.length > 0) {
    out.text = parsed.bareTokens.join(" ");
  }

  return out;
}
